import React, { useState, useEffect } from 'react';
import { LoadingGrid, SimpleAlert, TableList } from '../../../../rbts/helpers';
import {
  obtenerGrupos,
  registroGrupalService,
  AsistenteGrupo,
} from '../../../service/grupos/Grupos';

type Props = {};

/**
 * Convierte líneas "nombre,telefono" (CSV simple o pegado de Excel) en
 * la lista de asistentes. Ignora líneas vacías y un posible encabezado.
 */
const parsearAsistentes = (texto: string): AsistenteGrupo[] => {
  return texto
    .split(/\r?\n/)
    .map((linea) => linea.trim())
    .filter((linea) => linea.length > 0)
    .filter((linea, index) => !(index === 0 && /nombre/i.test(linea)))
    .map((linea) => {
      const [nombre, telefono] = linea.split(/[,;\t]/).map((c) => c?.trim());
      return { nombre: nombre || '', telefono: telefono || undefined };
    })
    .filter((asistente) => asistente.nombre.length > 0);
};

const View: React.FC<Props> = ({}) => {
  const [grupos, setGrupos] = useState<any[]>([]);
  const [loadingGrupos, setLoadingGrupos] = useState<boolean>(true);
  const [reload, setReload] = useState<number>(0);

  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<'EMPRESA' | 'ESCUELA' | 'OTRO'>('EMPRESA');
  const [contactoNombre, setContactoNombre] = useState('');
  const [contactoEmail, setContactoEmail] = useState('');
  const [textoAsistentes, setTextoAsistentes] = useState('');
  const [enviando, setEnviando] = useState(false);

  const asistentes = parsearAsistentes(textoAsistentes);

  useEffect(() => {
    setLoadingGrupos(true);
    obtenerGrupos()
      .then(setGrupos)
      .catch((error) => console.error('Error al obtener grupos:', error))
      .finally(() => setLoadingGrupos(false));
  }, [reload]);

  const cargarArchivo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setTextoAsistentes(String(reader.result ?? ''));
    reader.readAsText(file);
    event.target.value = '';
  };

  const registrar = async () => {
    if (!nombre.trim()) {
      SimpleAlert('El nombre del grupo es requerido', 3000, 'error');
      return;
    }
    if (asistentes.length === 0) {
      SimpleAlert('Agrega al menos un asistente (nombre,teléfono por línea)', 3000, 'error');
      return;
    }
    setEnviando(true);
    try {
      const result = await registroGrupalService({
        nombre: nombre.trim(),
        tipo,
        contacto_nombre: contactoNombre.trim() || undefined,
        contacto_email: contactoEmail.trim() || undefined,
        asistentes,
      });
      SimpleAlert(
        `Grupo registrado: ${result.total_asistentes} asistentes`,
        4000,
        'success'
      );
      setNombre('');
      setContactoNombre('');
      setContactoEmail('');
      setTextoAsistentes('');
      setReload(reload + 1);
    } catch (error: any) {
      if (error && error.response) {
        const { message } = error.response.data;
        SimpleAlert(message, 3000, 'error');
      }
    } finally {
      setEnviando(false);
    }
  };

  const columns: any = [
    {
      id: 'nombre',
      name: <div className="fw-bolder text-muted me-5">Grupo</div>,
      selector: (row: any) => row.nombre,
      cell: (row: any) => <span className="fw-bolder">{row.nombre}</span>,
      sortable: true,
      width: '30%',
    },
    {
      id: 'tipo',
      name: <div className="fw-bolder text-muted me-5">Tipo</div>,
      selector: (row: any) => row.tipo,
      sortable: true,
      width: '15%',
    },
    {
      id: 'contacto',
      name: <div className="fw-bolder text-muted me-5">Contacto</div>,
      selector: (row: any) => row.contacto_email ?? '',
      cell: (row: any) => (
        <span>
          {row.contacto_nombre ?? ''}
          {row.contacto_email ? ` (${row.contacto_email})` : ''}
        </span>
      ),
      sortable: true,
      width: '35%',
    },
    {
      id: 'asistentes',
      name: <div className="fw-bolder text-muted me-5">Asistentes</div>,
      selector: (row: any) => row.total_asistentes,
      sortable: true,
      width: '20%',
    },
  ];

  return (
    <div className="d-flex flex-column flex-xl-row">
      <div className="col-xl-12">
        <div className="flex-lg-row-fluid">
          <div className={`card card-xxl-stretch mb-xl-12`}>
            <div className="card-header border-0 pt-5 mt-4">
              <h3 className="card-title fw-bolder">Nuevo registro grupal</h3>
            </div>
            <div className="card-body">
              <div className="row mb-5">
                <div className="col-md-4">
                  <label className="required form-label fw-bolder">
                    Nombre del grupo (empresa/escuela)
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-solid"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bolder">Tipo</label>
                  <select
                    className="form-select form-select-solid"
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value as any)}
                  >
                    <option value="EMPRESA">Empresa</option>
                    <option value="ESCUELA">Escuela</option>
                    <option value="OTRO">Otro</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bolder">Nombre del contacto</label>
                  <input
                    type="text"
                    className="form-control form-control-solid"
                    value={contactoNombre}
                    onChange={(e) => setContactoNombre(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bolder">Correo del contacto</label>
                  <input
                    type="email"
                    className="form-control form-control-solid"
                    value={contactoEmail}
                    onChange={(e) => setContactoEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="row mb-5">
                <div className="col-md-8">
                  <label className="required form-label fw-bolder">
                    Asistentes — una línea por asistente: nombre,teléfono
                  </label>
                  <textarea
                    className="form-control form-control-solid"
                    rows={6}
                    placeholder={'Juan Pérez,8112345678\nMaría López,8187654321'}
                    value={textoAsistentes}
                    onChange={(e) => setTextoAsistentes(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bolder">O carga un archivo CSV</label>
                  <input
                    type="file"
                    accept=".csv,.txt"
                    className="form-control form-control-solid"
                    onChange={cargarArchivo}
                  />
                  <div className="text-muted fs-7 mt-2">
                    {asistentes.length > 0
                      ? `${asistentes.length} asistentes detectados`
                      : 'Sin asistentes detectados'}
                  </div>
                </div>
              </div>
              <div className="text-end">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={enviando}
                  onClick={registrar}
                >
                  {!enviando ? (
                    <span className="indicator-label">
                      Registrar grupo ({asistentes.length})
                    </span>
                  ) : (
                    <span className="indicator-progress" style={{ display: 'block' }}>
                      Registrando...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className={`card card-xxl-stretch mb-xl-12`}>
            <div className="card-header border-0 pt-5 mt-4">
              <h3 className="card-title fw-bolder">Grupos registrados</h3>
            </div>
            <div className="card-body">
              {loadingGrupos ? (
                <LoadingGrid text="Cargando..."></LoadingGrid>
              ) : (
                <TableList data={grupos} columns={columns}></TableList>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { View };
